// src/components/lesson/diagrams/DotPlot.tsx
//
// Dot plot (line plot) for SAT "One-Variable Data" questions: stacked dots over
// a number line showing a small frequency distribution (mode, median, range,
// mean from a picture).
//
// Params (provide ONE of):
//   values?: number[]                 // raw values; repeats stack
//   data?: { value: number, count: number }[]
// Plus:
//   axisMin?, axisMax?, step?         // axis range + tick spacing (inferred if absent)
//   label?: string

type DotPlotParams = {
  values?: number[]
  data?: { value: number; count: number }[]
  axisMin?: number
  axisMax?: number
  step?: number
  label?: string
}

const W = 360
const H = 180
const PAD = 28
const AXIS_Y = 150
const DOT_R = 4.5
const DOT_GAP = 11
const dotColor = 'rgb(34 211 238)'
const axisColor = 'rgb(100 116 139)'
const num = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) ? v : d)

export default function DotPlot({ params }: { params: Record<string, unknown> }) {
  const p = params as DotPlotParams

  // Build a value→count map from either `data` or raw `values`.
  const counts = new Map<number, number>()
  if (Array.isArray(p.data)) {
    for (const d of p.data) {
      if (typeof d?.value === 'number' && Number.isFinite(d.value)) {
        counts.set(d.value, (counts.get(d.value) ?? 0) + num(d.count, 1))
      }
    }
  } else if (Array.isArray(p.values)) {
    for (const v of p.values) {
      if (typeof v === 'number' && Number.isFinite(v)) counts.set(v, (counts.get(v) ?? 0) + 1)
    }
  }
  const entries = [...counts.entries()].sort((a, b) => a[0] - b[0])

  const allValues = entries.map((e) => e[0])
  const lo = num(p.axisMin, allValues.length ? Math.min(...allValues) : 0)
  const hi = num(p.axisMax, allValues.length ? Math.max(...allValues) : 10)
  const span = hi - lo || 1
  const step = num(p.step, niceStep(span / 8))

  const vx = (v: number) => PAD + ((v - lo) / span) * (W - 2 * PAD)

  const ticks: number[] = []
  for (let t = Math.ceil(lo / step) * step; t <= hi + 1e-9; t += step)
    ticks.push(Math.round(t * 100) / 100)

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={p.label ?? 'Dot plot'}
      className="my-3 w-full max-w-md rounded-md bg-card/60 ring-1 ring-slate-800"
    >
      {/* Axis */}
      <line
        x1={PAD - 4}
        y1={AXIS_Y}
        x2={W - PAD + 4}
        y2={AXIS_Y}
        stroke={axisColor}
        strokeWidth="1"
      />
      {ticks.map((t, i) => (
        <g key={`tk-${i}`}>
          <line
            x1={vx(t)}
            y1={AXIS_Y}
            x2={vx(t)}
            y2={AXIS_Y + 4}
            stroke={axisColor}
            strokeWidth="1"
          />
          <text x={vx(t)} y={AXIS_Y + 15} fontSize="9" textAnchor="middle" fill={axisColor}>
            {t}
          </text>
        </g>
      ))}

      {/* Stacked dots */}
      {entries.map(([value, count]) =>
        Array.from({ length: count }, (_, j) => (
          <circle
            key={`d-${value}-${j}`}
            cx={vx(value)}
            cy={AXIS_Y - 10 - j * DOT_GAP}
            r={DOT_R}
            fill={dotColor}
            stroke="rgb(15 23 42)"
            strokeWidth="1"
          />
        )),
      )}
    </svg>
  )
}

function niceStep(raw: number): number {
  if (!Number.isFinite(raw) || raw <= 0) return 1
  const pow = Math.pow(10, Math.floor(Math.log10(raw)))
  const f = raw / pow
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10
  return nice * pow
}
